import API from './api';
import { getActiveCompanyId, onCompanyReset } from '../company/session';

/**
 * Every tag assignment of a model type (Client, User, Store) in ONE request,
 * `GET /tags/assignments?modelType=…` (cached in the API's Redis), shared by the screen's table
 * and the tags shown in each of its rows. Before, a screen asked for each tag's items and then
 * for each row's tags: dozens of requests per page.
 *
 * Kept for 30 s per company and model type; `invalidateTagAssignments` drops it after a change.
 * An API without the endpoint yields null and callers use the per-tag requests as before.
 */

const TTL_MS = 30_000;
const cache = new Map(); // `${companyId}:${modelType}` -> { at, promise }

onCompanyReset(() => cache.clear());

const keyFor = (modelType) => `${getActiveCompanyId()}:${modelType}`;

/**
 * @param {string} modelType
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<Map<string, Object[]>|null>} modelId -> tagged items (`tagId` is the tag), or null
 */
export function getTagAssignments(modelType, { force = false } = {}) {
    const key = keyFor(modelType);
    const hit = cache.get(key);
    if (!force && hit && Date.now() - hit.at < TTL_MS) return hit.promise;

    const promise = API.Instance()
        .requestTagAssignments(modelType)
        .then((response) => {
            if (!response || !Array.isArray(response.items)) return null;
            const byItem = new Map();
            for (const item of response.items) {
                const id = String(item.modelId);
                if (!byItem.has(id)) byItem.set(id, []);
                byItem.get(id).push(item);
            }
            return byItem;
        })
        .catch(() => null);
    cache.set(key, { at: Date.now(), promise });
    promise.then((result) => {
        // Don't keep a failure around: the next caller tries again.
        if (result === null && cache.get(key)?.promise === promise) cache.delete(key);
    });
    return promise;
}

export function invalidateTagAssignments(modelType) {
    cache.delete(keyFor(modelType));
}

/**
 * `{ [rowId]: tag[] }` for a table's rows. `tags` is the model type's tag list (used by the
 * per-tag fallback on APIs without the batch endpoint).
 */
export async function tagsMapFor(rows, modelType, tags) {
    const tagsMap = {};
    (rows || []).forEach((row) => {
        tagsMap[row._id] = [];
    });

    const byItem = await getTagAssignments(modelType);
    if (byItem) {
        Object.keys(tagsMap).forEach((id) => {
            tagsMap[id] = (byItem.get(String(id)) || []).map((item) => item.tagId).filter(Boolean);
        });
        return tagsMap;
    }

    const api = API.Instance();
    await Promise.all(
        (tags || []).map(async (tag) => {
            try {
                const itemsResponse = await api.requestTagItems(tag._id);
                (itemsResponse.items || []).forEach((item) => {
                    if (tagsMap[item.modelId]) tagsMap[item.modelId].push(tag);
                });
            } catch (err) {
                console.error(`Error fetching items for tag ${tag.name}:`, err);
            }
        })
    );
    return tagsMap;
}
